import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio/react';

import { DrawerClose } from '@paid-ui/components/drawer';
import { IconButton } from '@paid-ui/components/icon-button';
import { LoadingIndicator } from '@paid-ui/components/loading-indicator';
import { Overview, OverviewItem } from '@paid-ui/components/overview';
import { Tooltip } from '@paid-ui/components/tooltip';
import { CloseIcon } from '@paid-ui/icons/close';
import { InfoIcon } from '@paid-ui/icons/info';
import { contractManager } from '@paid-ui/models/contract';
import { userManager } from '@paid-ui/models/user';

import { useTimelinesUpdate } from './hooks';

const TimelineDetails: React.FC = () => {
  const { impersonation } = useSnapshot(userManager);
  const {
    currentContract,
    hasWorkCompleted,
    commencementDate,
    anticipatedCommencementDate,
    anticipatedCompletionDate,
    contractualCompletionDate,
    actualCompletionDate,
    constructionDays,
    initialConstructionDays,
    inclementWeather,
    weekendDays,
    weekdayPublicHolidays,
    rosteredDaysOff,
    foreseeableBreaks,
    builderHolidays,
    additionalDaysFromVariations,
    reasonableDelays,
    totalTimeExtension,
  } = useSnapshot(contractManager);

  const [value, setValue] = useState(initialConstructionDays.toString());
  const { mutate, isError, isLoading: isSubmitting } = useTimelinesUpdate();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.currentTarget.value ?? '');
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    mutate({
      initialConstructionDays: Number(event.currentTarget.value || 0),
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.code === 'Enter') {
      mutate({
        initialConstructionDays: Number(event.currentTarget.value || 0),
      });
    }
  };

  useEffect(() => {
    if (!currentContract) {
      return;
    }
    setValue(initialConstructionDays.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(currentContract)]);

  useEffect(() => {
    if (isError) {
      setValue(initialConstructionDays.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  if (!currentContract) {
    return <LoadingIndicator />;
  }

  if (hasWorkCompleted) {
    return (
      <div className="flex h-full w-full flex-col overflow-auto bg-grey-lightest">
        <header className="sticky top-0 z-20 flex h-16 w-full shrink-0 items-center justify-between border-b border-grey-hover bg-white px-6 py-4 sm:px-9">
          <DrawerClose tabIndex={-1}>
            <IconButton variant="subtle" className="shrink-0 text-grey-dark">
              <CloseIcon />
            </IconButton>
          </DrawerClose>
          <div className="flex flex-1 items-center justify-center gap-x-1.5 text-center text-sm font-medium">
            Timeline details
          </div>
          <span className="h-7 w-7" />
        </header>
        <div className="z-10 flex flex-1 flex-col p-6 sm:px-9 sm:py-8">
          <Overview className="bg-white">
            <OverviewItem label="Dates" className="rounded-t bg-grey-light" />
            <OverviewItem
              size="sm"
              label={
                <div className="flex items-center gap-x-1.5">
                  <span>Commencement date</span>
                  <span className="shrink-0 font-normal">·</span>
                  <span className="font-normal text-grey-tertiary">Actual</span>
                  <Tooltip title="The confirmed date construction officially began, as advised by a formal notice.">
                    <span className="shrink-0 cursor-pointer text-grey-tertiary transition-colors hover:text-grey-dark">
                      <InfoIcon className="h-3.5 w-3.5" />
                    </span>
                  </Tooltip>
                </div>
              }
            >
              <div className="text-right">{commencementDate?.format('DD MMM YYYY') ?? '-'}</div>
            </OverviewItem>
            <OverviewItem
              size="sm"
              label={
                <div className="flex items-center gap-x-1.5">
                  <span>Completion date</span>
                  <span className="shrink-0 font-normal">·</span>
                  <span className="font-normal text-grey-tertiary">Actual</span>
                  <Tooltip title="The confirmed date construction officially finished, as advised by a formal notice.">
                    <span className="shrink-0 cursor-pointer text-grey-tertiary transition-colors hover:text-grey-dark">
                      <InfoIcon className="h-3.5 w-3.5" />
                    </span>
                  </Tooltip>
                </div>
              }
            >
              <div className="text-right">{actualCompletionDate?.format('DD MMM YYYY') ?? '-'}</div>
            </OverviewItem>
            <OverviewItem label="Total" className="rounded-b bg-grey-light">
              <div className="text-right font-medium">{constructionDays} calendar days</div>
            </OverviewItem>
          </Overview>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col overflow-auto bg-grey-lightest">
      <header className="sticky top-0 z-20 flex h-16 w-full shrink-0 items-center justify-between border-b border-grey-hover bg-white px-6 py-4 sm:px-9">
        <DrawerClose tabIndex={-1}>
          <IconButton variant="subtle" className="shrink-0 text-grey-dark">
            <CloseIcon />
          </IconButton>
        </DrawerClose>
        <div className="flex flex-1 items-center justify-center gap-x-1.5 text-center text-sm font-medium">
          Timeline details
        </div>
        <span className="h-7 w-7" />
      </header>
      <div className="z-10 flex flex-1 flex-col gap-y-9 p-6 sm:px-9 sm:py-8">
        <Overview className="bg-white">
          <OverviewItem label="Dates" className="rounded-t bg-grey-light" />
          {commencementDate ? (
            <OverviewItem
              size="sm"
              label={
                <div className="flex items-center gap-x-1.5">
                  <span>Commencement date</span>
                  <span className="shrink-0 font-normal">·</span>
                  <span className="font-normal text-grey-tertiary">Actual</span>
                  <Tooltip title="The confirmed date construction officially began, as advised by a formal notice.">
                    <span className="shrink-0 cursor-pointer text-grey-tertiary transition-colors hover:text-grey-dark">
                      <InfoIcon className="h-3.5 w-3.5" />
                    </span>
                  </Tooltip>
                </div>
              }
            >
              <div className="text-right">{commencementDate?.format('DD MMM YYYY') ?? '-'}</div>
            </OverviewItem>
          ) : (
            <OverviewItem
              size="sm"
              label={
                <div className="flex items-center gap-x-1.5">
                  <span>Commencement date</span>
                  <span className="shrink-0 font-normal">·</span>
                  <span className="font-normal text-grey-tertiary">Anticipated</span>
                  <Tooltip title="The estimated start date based on the contract, assuming works have not officially begun.">
                    <span className="shrink-0 cursor-pointer text-grey-tertiary transition-colors hover:text-grey-dark">
                      <InfoIcon className="h-3.5 w-3.5" />
                    </span>
                  </Tooltip>
                </div>
              }
            >
              {anticipatedCommencementDate ? (
                <div className="text-right">
                  {anticipatedCommencementDate?.format('DD MMM YYYY') ?? '-'}
                </div>
              ) : (
                <div className="text-right text-grey-tertiary">Not set</div>
              )}
            </OverviewItem>
          )}
          {actualCompletionDate ? (
            <OverviewItem
              size="sm"
              label={
                <div className="flex items-center gap-x-1.5">
                  <span>Completion date</span>
                  <span className="shrink-0 font-normal">·</span>
                  <span className="font-normal text-grey-tertiary">Actual</span>
                  <Tooltip title="The confirmed date construction officially finished, as advised by a formal notice.">
                    <span className="shrink-0 cursor-pointer text-grey-tertiary transition-colors hover:text-grey-dark">
                      <InfoIcon className="h-3.5 w-3.5" />
                    </span>
                  </Tooltip>
                </div>
              }
            >
              <div className="text-right">{actualCompletionDate.format('DD MMM YYYY') ?? '-'}</div>
            </OverviewItem>
          ) : contractualCompletionDate ? (
            <OverviewItem
              size="sm"
              label={
                <div className="flex items-center gap-x-1.5">
                  <span>Completion date</span>
                  <span className="shrink-0 font-normal">·</span>
                  <span className="font-normal text-grey-tertiary">Contractual</span>
                  <Tooltip title="The completion date based on the actual commencement and construction period.">
                    <span className="shrink-0 cursor-pointer text-grey-tertiary transition-colors hover:text-grey-dark">
                      <InfoIcon className="h-3.5 w-3.5" />
                    </span>
                  </Tooltip>
                </div>
              }
            >
              <div className="text-right">
                {contractualCompletionDate.format('DD MMM YYYY') ?? '-'}
              </div>
            </OverviewItem>
          ) : (
            <OverviewItem
              size="sm"
              label={
                <div className="flex items-center gap-x-1.5">
                  <span>Completion date</span>
                  <span className="shrink-0 font-normal">·</span>
                  <span className="font-normal text-grey-tertiary">Anticipated</span>
                  <Tooltip title="The estimated completion date based on the contract, assuming works have not officially begun.">
                    <span className="shrink-0 cursor-pointer text-grey-tertiary transition-colors hover:text-grey-dark">
                      <InfoIcon className="h-3.5 w-3.5" />
                    </span>
                  </Tooltip>
                </div>
              }
            >
              {anticipatedCompletionDate ? (
                <div className="text-right">
                  {anticipatedCompletionDate.format('DD MMM YYYY') ?? '-'}
                </div>
              ) : (
                <div className="text-right text-grey-tertiary">Not set</div>
              )}
            </OverviewItem>
          )}
        </Overview>
        <Overview className="bg-white">
          <OverviewItem label="Work period" className="rounded-t bg-grey-light">
            <div className="text-right text-sm text-grey-tertiary">in calendar days</div>
          </OverviewItem>
          <OverviewItem size="sm" label="Initial construction days">
            <div className="text-right">
              {impersonation ? (
                <input
                  placeholder="0"
                  disabled={isSubmitting}
                  value={value}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown}
                  className="-mx-1.5 -my-1 appearance-none rounded border border-grey-mid bg-transparent px-1.5 py-1 text-right outline-none transition-colors focus:border-blue focus:bg-white focus:shadow-field disabled:cursor-not-allowed disabled:bg-grey-light disabled:text-grey-dark"
                />
              ) : (
                initialConstructionDays
              )}
            </div>
          </OverviewItem>
          <OverviewItem size="sm" label="Inclement weather" hidden={inclementWeather === 0}>
            <div className="text-right">{inclementWeather}</div>
          </OverviewItem>
          <OverviewItem size="sm" label="Weekend days" hidden={weekendDays === 0}>
            <div className="text-right">{weekendDays}</div>
          </OverviewItem>
          <OverviewItem
            size="sm"
            label="Weekday public holidays"
            hidden={weekdayPublicHolidays === 0}
          >
            <div className="text-right">{weekdayPublicHolidays}</div>
          </OverviewItem>
          <OverviewItem size="sm" label="Rostered days off" hidden={rosteredDaysOff === 0}>
            <div className="text-right">{rosteredDaysOff}</div>
          </OverviewItem>
          <OverviewItem size="sm" label="Foreseeable breaks" hidden={foreseeableBreaks === 0}>
            <div className="text-right">{foreseeableBreaks}</div>
          </OverviewItem>
          <OverviewItem size="sm" label="Builder holidays" hidden={builderHolidays === 0}>
            <div className="text-right">{builderHolidays}</div>
          </OverviewItem>
          <OverviewItem
            size="sm"
            label="Additional days from variations"
            hidden={additionalDaysFromVariations === 0}
          >
            <div className="text-right">{additionalDaysFromVariations}</div>
          </OverviewItem>
          <OverviewItem
            size="sm"
            hidden={!reasonableDelays?.days}
            label={
              <div className="inline-flex items-center gap-x-1.5">
                <span>Reasonable delays</span>
                <Tooltip
                  hidden={!reasonableDelays?.description}
                  title={reasonableDelays?.description}
                >
                  <span className="cursor-pointer text-grey-tertiary transition-colors hover:text-grey-dark">
                    <InfoIcon className="h-3.5 w-3.5" />
                  </span>
                </Tooltip>
              </div>
            }
          >
            <div className="text-right">{reasonableDelays?.days ?? 0}</div>
          </OverviewItem>
          <OverviewItem
            size="sm"
            label={
              <div className="inline-flex items-center gap-x-1.5">
                <span>Total time extension</span>
                <Tooltip title="Time extension from variations">
                  <span className="cursor-pointer text-grey-tertiary transition-colors hover:text-grey-dark">
                    <InfoIcon className="h-3.5 w-3.5" />
                  </span>
                </Tooltip>
              </div>
            }
          >
            <div className="text-right">{totalTimeExtension}</div>
          </OverviewItem>
          <OverviewItem label="Total" className="rounded-b bg-grey-light">
            <div className="text-right font-medium">{constructionDays} calendar days</div>
          </OverviewItem>
        </Overview>
      </div>
    </div>
  );
};

export default TimelineDetails;
